import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb, { type Crumb } from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ShareActions from "@/pages/detail/components/ShareActions";
import {
  categoryAccent,
  formatDate,
  formatViews,
  getArticle,
  getCategoryName,
  relatedArticles,
} from "@/utils/news";

export default function DetailPage() {
  const { id = "" } = useParams();
  const article = getArticle(id);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto w-full px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-4xl text-foreground-400">
          <i className="ri-error-warning-line" />
        </div>
        <h1 className="mt-5 text-xl font-heading font-black text-foreground-950">
          抱歉，这条爆料走丢了
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          内容可能已被删除，或链接地址有误。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
          <Link
            to="/category/all"
            className="px-4 py-2 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            浏览全部爆料
          </Link>
        </div>
      </div>
    );
  }

  const related = relatedArticles(article, 4);
  const crumbs: Crumb[] = [
    {
      label: `${getCategoryName(article.category)}爆料`,
      to: `/category/${article.category}`,
    },
    { label: article.subCategory },
  ];

  return (
    <div>
      <Seo
        title={`${article.title} - 详情 - 51爆料`}
        description={article.summary.slice(0, 80)}
        keywords={`${article.tags.join(",")},51爆料,爆料详情`}
      />

      <article className="max-w-7xl mx-auto w-full px-4 md:px-6 py-6">
        <Breadcrumb items={crumbs} />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 mt-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span
                className={`text-xs px-2.5 py-1 rounded-full ${categoryAccent(
                  article.category,
                )}`}
              >
                {getCategoryName(article.category)}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600">
                {article.subCategory}
              </span>
              {article.hot && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-accent-500 text-foreground-950 flex items-center gap-0.5">
                  <i className="ri-fire-line" />
                  热爆
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-black text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 pb-5 border-b border-background-200 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次浏览
              </span>
            </div>

            <div className="w-full h-56 md:h-96 rounded-lg overflow-hidden my-6">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 51爆料`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-none">
              {article.content.map((para, i) => (
                <div key={i}>
                  <p className="text-base text-foreground-700 leading-8 mb-5">
                    {para}
                  </p>
                  {i === 1 && (
                    <blockquote className="my-6 pl-4 border-l-4 border-primary-500 bg-primary-50 rounded-r-md py-4 pr-4">
                      <p className="text-base text-primary-900 italic leading-7">
                        {article.quote}
                      </p>
                    </blockquote>
                  )}
                  {i === 2 && (
                    <div className="w-full h-48 md:h-72 rounded-lg overflow-hidden my-6">
                      <img
                        src={article.image}
                        alt={`${article.title} 相关配图`}
                        title={`${article.title} 现场图`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              {article.tags.map((t) => (
                <span
                  key={t}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900"
                >
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <ShareActions title={article.title} />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                返回{getCategoryName(article.category)}频道
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer"
              >
                <i className="ri-home-4-line" />
                回到首页
              </Link>
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950 mb-4">
                <i className="ri-links-line text-primary-500" />
                相关爆料推荐
              </h2>
              {related.length > 0 ? (
                <ul className="space-y-4">
                  {related.map((a) => (
                    <li key={a.id} className="group">
                      <Link
                        to={`/detail/${a.id}`}
                        className="block cursor-pointer"
                      >
                        <div className="w-full h-28 rounded-md overflow-hidden mb-2">
                          <img
                            src={a.image}
                            alt={a.title}
                            title={`${a.title} - 51爆料`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {a.title}
                        </span>
                        <span className="text-xs text-foreground-400 mt-1 block">
                          {formatDate(a.date)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-foreground-500">暂无相关推荐。</p>
              )}
            </div>

            <div className="bg-primary-500 rounded-lg p-5 text-white">
              <h2 className="flex items-center gap-2 text-base font-heading font-bold mb-2">
                <i className="ri-megaphone-line" />
                你觉得有更猛的料？
              </h2>
              <p className="text-sm text-white/85 leading-relaxed">
                爆料不止于阅读。把你掌握的线索提交给我们，一起还原事件全貌。
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1 mt-4 px-4 py-2 rounded-md bg-white text-primary-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                我要爆料
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="text-lg md:text-xl font-heading font-black text-foreground-950 mb-5">
              继续浏览
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((a) => (
                <ArticleCard key={`bottom-${a.id}`} article={a} layout="column" />
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}